import { useQuery, gql } from '@apollo/client';

import useSession from 'core/hooks/use-session';
import { isBranchEnv } from 'brand-config';
import { buildBixAutoUpsellInput } from 'common/components/branch-auto-upsell';

const ASSURITY_LIFE_ELIGIBILITY_QUERY = gql`
  query ($offerId: String) {
    assurityLifeCheckEligibility(args: { branchOfferId: $offerId }) {
      __typename
      ... on AssurityLifeCheckEligibilitySuccessResponse {
        staffUserCapabilities
        eligibleLifeOffers {
          benefitAmount
          monthlyPremiumAmount
          annualPremiumAmount
          personId
        }
      }
    }
  }
`;

/**
 * Which offers the "Additional offers" section shows for the offer in the store, and the props
 * `AdditionalOffersSection` needs to render them. Shared by today's sidebar and the Quote Revisit
 * left column (#36353), which both render that section unchanged.
 */
const useAdditionalOffers = (store) => {
  const { viewOnly } = useSession();

  const assurityLifeEligibilityQuery = useQuery(ASSURITY_LIFE_ELIGIBILITY_QUERY, {
    variables: { offerId: store?.offer?.id },
    skip: !store?.offer?.id
  });

  const shouldShowAssurityTermLifeAdditionalOffer = Boolean(
    !assurityLifeEligibilityQuery.loading &&
      assurityLifeEligibilityQuery.data?.assurityLifeCheckEligibility.__typename !==
        'AssurityLifeCheckEligibilityStaffUserNotEligibleResponse'
  );

  // BIX auto upsell — only on non-Branch carrier farm offers with a usable primary
  // applicant + farmLocations[0] address. Gated on `!viewOnly` so view-only operators
  // don't trigger the mount-time mutation. (We can't use `canEditOffers` here — that's
  // a Branch-staff role check; mutual carrier agents aren't in those groups.) The
  // agent-eligibility check (`branch_play = "Mutuals Accommodation"` lookup) lives
  // server-side in the `create-bix-auto-offer-upsell` lambda — see GitHub issue #27759.
  const bixAutoUpsellInput =
    !viewOnly && !isBranchEnv && store.isFarmOffer && store?.offer?.id
      ? buildBixAutoUpsellInput({
          branchOfferId: store.offer.id,
          applicant: store?.offer?.quote?.drivers?.find((d) => d.isPrimary),
          farmAddress: store?.offer?.quote?.farmLocations?.[0]?.address,
          email: store?.offer?.quote?.email,
          phone: store?.offer?.quote?.phone
        })
      : null;

  const assurityTermLife = shouldShowAssurityTermLifeAdditionalOffer && !store.isFarmOffer;

  return {
    loading: assurityLifeEligibilityQuery.loading,
    shouldShowAdditionalOffers: assurityTermLife || !!bixAutoUpsellInput,
    additionalOffersProps: {
      assurityTermLife,
      assurityTermLifeData: {
        ...assurityLifeEligibilityQuery.data?.assurityLifeCheckEligibility,
        branchOfferId: store?.offer?.id,
        email: store?.offer?.quote.email
      },
      bixAutoUpsellInput
    }
  };
};

export default useAdditionalOffers;
